import { Component, EventEmitter, Input, input, OnDestroy, output, Output, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { emitValueIfValid, LoadableStatus, mapToLoadable } from '@aiwyn/shared';
import { AbstractPaymentMethod, PaymentFlow, PaymentMethodIntent, PaymentMethodMode } from '../../../models';
import { PaymentMethodApiService, PaymentMethodType } from '@aiwyn/api';
import { map, take } from 'rxjs/operators';
import { MatAccordion } from '@angular/material/expansion';
import { PaymentPlatformService } from '../../../services';
import { StripePlatformService } from '../../services';

@Component({
  selector: 'app-stripe-select-payment-method',
  templateUrl: './stripe-select-payment-method.component.html',
  styleUrls: ['./stripe-select-payment-method.component.scss'],
  standalone: false,
})
export class StripeSelectPaymentMethodComponent implements OnDestroy {
  /**
   * Make note bout dis.
   */
  intent$ = new BehaviorSubject<PaymentMethodIntent>(undefined);
  @Input() set intent(intent: PaymentMethodIntent) {
    emitValueIfValid(intent, this.intent$);
  }

  /**
   * If intent is 'payment', this will be used to determine the payment flow.
   */
  paymentFlow$ = new BehaviorSubject<PaymentFlow>(undefined);
  @Input() set paymentFlow(paymentFlow: PaymentFlow) {
    emitValueIfValid(paymentFlow, this.paymentFlow$);
  }

  /**
   *
   */
  selectedPaymentMethodId$ = new BehaviorSubject<string>(undefined);
  @Input() set selectedPaymentMethodId(selectedPaymentMethodId: string) {
    emitValueIfValid(selectedPaymentMethodId, this.selectedPaymentMethodId$);
  }

  /**
   * If intent is 'payment', this will be used to determine the payment flow.
   */
  paymentMethodMode$ = new BehaviorSubject<PaymentMethodMode>(undefined);
  @Input() set paymentMethodMode(paymentMethodMode: PaymentMethodMode) {
    emitValueIfValid(paymentMethodMode, this.paymentMethodMode$);
  }

  /**
   * This will only be true in the engagement wizard's payment info step
   * when there is no prepayment to be made and adding a payment method to the engagement is optional.
   */
  setUpLaterEnabled$ = new BehaviorSubject<boolean>(false);
  @Input() set setUpLaterEnabled(setUpLaterEnabled: boolean) {
    emitValueIfValid(setUpLaterEnabled, this.setUpLaterEnabled$);
  }

  @Output() paymentMethodModeChange = new EventEmitter<PaymentMethodMode>();
  @Output() paymentMethodSelected = new EventEmitter<AbstractPaymentMethod>();
  newPaymentMethodTypeSelected = output<PaymentMethodType>();
  updateBillingAddress = output<AbstractPaymentMethod>();

  // Email-first gate over the creation area only (saved methods stay outside it). `emailGateActive` = the
  // flag is on; `emailGateLocked` = no valid email yet. lockedNewMethodAttempt bubbles a click on the locked
  // creation area up to the host so it can focus the email field.
  emailGateActive = input(false);
  emailGateLocked = input(false);
  // Email prepopulated and untouched — suppresses the gate status banner.
  emailGatePrepopulated = input(false);
  lockedNewMethodAttempt = output<void>();

  readonly PaymentMethodIntent = PaymentMethodIntent;
  readonly PaymentFlow = PaymentFlow;
  readonly PaymentMethodMode = PaymentMethodMode;
  readonly PaymentMethodType = PaymentMethodType;
  readonly LoadableStatus = LoadableStatus;

  private readonly _unsubscribe$ = new Subject<void>();

  @ViewChild('setUpLaterAccordion') setUpLaterAccordion: MatAccordion;

  showSetUpLater$ = combineLatest([
    this.setUpLaterEnabled$,
    this.paymentPlatformService.getSavedPaymentMethodsExist(),
  ]).pipe(
    map(([setUpLaterEnabled, savedPaymentMethodsExist]) => setUpLaterEnabled && !savedPaymentMethodsExist),
  );

  showSavedPaymentMethods$ = combineLatest([
    this.intent$,
    this.paymentPlatformService.getSavedPaymentMethodsExist(),
  ]).pipe(
    map(([intent, savedPaymentMethodsExist]) => intent === PaymentMethodIntent.PAYMENT && savedPaymentMethodsExist),
  );

  temporaryPaymentMethod$ = this.paymentPlatformService.getTemporaryPaymentMethod();

  setupIntentClientSecretStatus$ = this.stripePlatformService.getSetupIntentClientSecretStatus();
  // TODO: Move this to state. It only needs to be called once per session.
  paymentMethodDisplayNames$ = this.paymentMethodApiService.getPaymentMethodDisplayNames().pipe(
    mapToLoadable(),
  );

  constructor(
    private readonly paymentPlatformService: PaymentPlatformService,
    private readonly stripePlatformService: StripePlatformService,
    private readonly paymentMethodApiService: PaymentMethodApiService,
  ) { }

  onNewPaymentMethodTypeSelected(selectedPaymentMethodType?: PaymentMethodType): void {
    this.newPaymentMethodTypeSelected.emit(selectedPaymentMethodType);
  }

  onUpdateBillingAddress(paymentMethod: AbstractPaymentMethod): void {
    this.updateBillingAddress.emit(paymentMethod);
  }

  removeTemporaryPaymentMethod(clickEvent: MouseEvent): void {
    clickEvent.stopPropagation();
    this.paymentPlatformService.removeTemporaryPaymentMethod().pipe(take(1)).subscribe();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
